import { type ReactNode } from 'react'
import EmptyState from './EmptyState'

/**
 * Arquetipo "Tarjetas" — grid de 3 columnas con título, pill de estado, cuerpo
 * opcional en caja hundida y filas clave-valor al pie.
 *
 * Lo usan Plantillas y speech, Conocimiento, Modelos e Integraciones: todos
 * casos donde el ítem tiene un cuerpo de texto que hay que poder leer entero,
 * y una tabla lo truncaría a una celda.
 */

export interface CardItem {
  key: string
  title: string
  subtitle?: string
  /** Pill arriba a la derecha. */
  status?: ReactNode
  /** Cuerpo largo (plantilla, fragmento de conocimiento). */
  body?: string
  /** Filas clave-valor al pie. */
  meta?: Array<{ label: string; value: string }>
  /** Acciones al pie de la tarjeta. */
  actions?: ReactNode
}

export default function CardGrid({ items, columns = 3, emptyTitle, emptyMessage }: {
  items: CardItem[]
  columns?: 2 | 3
  emptyTitle?: string
  emptyMessage?: string
}) {
  if (items.length === 0) {
    return <EmptyState title={emptyTitle ?? 'Sin elementos'} description={emptyMessage} />
  }

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {items.map((item) => (
        <div key={item.key} className="card flex flex-col p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-card font-extrabold text-ink">{item.title}</h3>
              {item.subtitle && (
                <p className="mt-px text-micro text-ink-muted">{item.subtitle}</p>
              )}
            </div>
            {item.status}
          </div>

          {item.body && (
            <div className="mt-3 whitespace-pre-wrap rounded-btn bg-surface px-3 py-2.5 text-body leading-relaxed text-ink-muted">
              {item.body}
            </div>
          )}

          {item.meta && item.meta.length > 0 && (
            <div className="mt-3 flex flex-col">
              {item.meta.map((fila) => (
                <div
                  key={fila.label}
                  className="flex items-center justify-between gap-3 border-t border-surface-subtle py-1.5 text-micro"
                >
                  <span className="text-ink-muted">{fila.label}</span>
                  <span className="font-mono text-ink">{fila.value}</span>
                </div>
              ))}
            </div>
          )}

          {item.actions && <div className="mt-3 flex gap-2">{item.actions}</div>}
        </div>
      ))}
    </div>
  )
}
